import { useMemo, useState, type ReactNode } from 'react'
import { Link } from 'react-router-dom'
import clsx from 'clsx'
import { ArrowDown, ArrowUp, Plus } from 'lucide-react'
import FigureValue from '@/components/dashboards/FigureValue'
import type { Figure } from '@/types/dashboards'
import {
  MEASURE_BY_KEY,
  drillForIds,
  groupLabel,
  idsForMeasure,
  sortRows,
  type DealFact,
  type MeasureKey,
  type Measures,
  type PivotRow,
} from '@/services/dashboardAnalytics'

export interface PivotColumn {
  key: MeasureKey
  /** Tailwind visibility, e.g. 'hidden 2xl:table-cell' for a secondary column. */
  className?: string
}

/** The full measure set, primary columns first. Secondary columns give way
 *  below 1536px so the table never forces a horizontal page scroll. */
export const DEFAULT_PIVOT_COLUMNS: PivotColumn[] = [
  { key: 'count' },
  { key: 'requested', className: 'hidden xl:table-cell' },
  { key: 'commitment' },
  { key: 'funded' },
  { key: 'fundedCount' },
  { key: 'activeCount', className: 'hidden 2xl:table-cell' },
  { key: 'signedCount', className: 'hidden 2xl:table-cell' },
  { key: 'deadCount', className: 'hidden 2xl:table-cell' },
  { key: 'quoteRatePct' },
  { key: 'conversionPct', className: 'hidden xl:table-cell' },
  { key: 'wtdLtvLtc' },
  { key: 'wtdCouponPct', className: 'hidden xl:table-cell' },
  { key: 'avgDaysToFund' },
]

interface PivotTableProps {
  rows: readonly PivotRow[]
  totals: Measures | null
  dimensionLabel: string
  /** The `by` the rows were asked with (outcome children make outcome drills exact). */
  by: string | null
  /** Facts for the cohort, keyed by id — make every subset drill exact. */
  facts?: ReadonlyMap<string, DealFact>
  columns?: PivotColumn[]
  /** Drill title prefix (the cohort). */
  scopeLabel?: string
  onSelectRow?: (key: string | null) => void
  /** Rows keep server order until a header is clicked (ordered dimensions). */
  ordered?: boolean
  /** Optional per-row action (e.g. open the person's profile). */
  rowAction?: (row: PivotRow) => ReactNode
  /** When set, the row label is a link (e.g. to a profile) instead of a
   *  cohort filter. */
  labelHref?: (row: PivotRow) => string | null
  /** Columns after the measures — values the pivot does not carry. */
  extraColumns?: Array<{ key: string; label: string; render: (row: PivotRow) => ReactNode; className?: string }>
  /** Leading rank column (1 = first row in the current sort). */
  showRank?: boolean
  /** Drop a measure column that no row (and no total) has a value in, and
   *  name it once under the table instead of printing a column of dashes. */
  hideEmptyColumns?: boolean
  /** Leave the "not shown" note to the host (it states it in its footer). */
  hideEmptyNote?: boolean
  className?: string
}

/** The measure columns no row (and no total) has a value in — pure, so a host
 *  can state them in its own footer instead of under the table. */
export function emptyMeasureColumns(
  rows: readonly PivotRow[],
  totals: Measures | null,
  columns: readonly PivotColumn[],
): PivotColumn[] {
  if (rows.length === 0) return []
  return columns.filter(
    (column) => !rows.some((row) => hasValue(row.measures, column.key)) && !hasValue(totals, column.key),
  )
}

function hasValue(measures: Measures | null | undefined, key: MeasureKey): boolean {
  if (!measures) return false
  const raw = (measures as unknown as Record<string, unknown>)[key]
  return typeof raw === 'number' && Number.isFinite(raw)
}

function cellFigure(
  measures: Measures,
  key: MeasureKey,
  ids: string[],
  title: string,
): Figure {
  const meta = MEASURE_BY_KEY[key]
  const raw = (measures as unknown as Record<string, unknown>)[key]
  const value = typeof raw === 'number' && Number.isFinite(raw) ? raw : null
  const coverage = meta.coverage ? (measures as unknown as Record<string, unknown>)[meta.coverage] : null
  const drill = value != null && value !== 0 ? drillForIds(ids, title) : null
  return {
    label: meta.label,
    value: meta.unit === 'days' && value != null ? Math.round(value) : value,
    format: meta.format,
    ...(drill ? { drill } : {}),
    ...(coverage != null ? { basis: String(coverage) } : {}),
  }
}

/**
 * Every measure for every group, sortable. Each number opens exactly the deals
 * it stands on — the funded count its funded deals, a weighted LTV the deals
 * that state one — and a row's label narrows the cohort to that group.
 */
export default function PivotTable({
  rows,
  totals,
  dimensionLabel,
  by,
  facts,
  columns: allColumns = DEFAULT_PIVOT_COLUMNS,
  scopeLabel,
  onSelectRow,
  ordered = false,
  rowAction,
  labelHref,
  extraColumns = [],
  showRank = false,
  hideEmptyColumns = true,
  hideEmptyNote = false,
  className,
}: PivotTableProps) {
  const { shownColumns, emptyColumns } = useMemo(() => {
    if (!hideEmptyColumns || rows.length === 0) return { shownColumns: allColumns, emptyColumns: [] as PivotColumn[] }
    const shown: PivotColumn[] = []
    const empty: PivotColumn[] = []
    for (const column of allColumns) {
      const any = rows.some((row) => hasValue(row.measures, column.key)) || hasValue(totals, column.key)
      ;(any ? shown : empty).push(column)
    }
    return { shownColumns: shown, emptyColumns: empty }
  }, [allColumns, rows, totals, hideEmptyColumns])
  const columns = shownColumns
  const [sort, setSort] = useState<{ key: MeasureKey | 'label'; dir: 'asc' | 'desc' } | null>(
    ordered ? null : { key: 'commitment', dir: 'desc' },
  )
  const sorted = useMemo(
    () => (sort ? sortRows(rows, sort.key, sort.dir) : [...rows]),
    [rows, sort],
  )
  const prefix = scopeLabel ? `${scopeLabel} · ` : ''

  const header = (key: MeasureKey | 'label', label: string, className?: string, align: 'left' | 'right' = 'right') => {
    const active = sort?.key === key
    return (
      <th
        key={key}
        scope="col"
        aria-sort={active ? (sort?.dir === 'asc' ? 'ascending' : 'descending') : 'none'}
        className={clsx('whitespace-nowrap !px-2', align === 'right' ? 'text-right' : 'text-left', className)}
      >
        <button
          type="button"
          onClick={() =>
            setSort((current) =>
              current?.key === key
                ? { key, dir: current.dir === 'desc' ? 'asc' : 'desc' }
                : { key, dir: key === 'label' ? 'asc' : 'desc' },
            )
          }
          className={clsx(
            'inline-flex items-center gap-0.5 uppercase hover:text-stone-200',
            active ? 'text-amber-200' : undefined,
          )}
        >
          {label}
          {active && (sort?.dir === 'asc' ? <ArrowUp size={9} /> : <ArrowDown size={9} />)}
        </button>
      </th>
    )
  }

  return (
    <div className={clsx('min-w-0 overflow-x-auto scrollbar-thin', className)}>
      <table className="dense-table">
        <thead className="sticky top-0 z-[1]">
          <tr>
            {showRank && <th scope="col" className="w-6 !px-2 text-right">#</th>}
            {header('label', dimensionLabel, 'min-w-[150px]', 'left')}
            {columns.map((column) => header(column.key, MEASURE_BY_KEY[column.key].label, column.className))}
            {extraColumns.map((column) => (
              <th key={column.key} scope="col" className={clsx('whitespace-nowrap !px-2 text-right', column.className)}>
                {column.label}
              </th>
            ))}
            {rowAction && <th className="!px-2" aria-label="Actions" />}
          </tr>
        </thead>
        <tbody>
          {sorted.map((row, rowIndex) => {
            const label = groupLabel(row.key, row.label)
            const href = labelHref?.(row) ?? null
            return (
              <tr key={row.key ?? '∅'}>
                {showRank && (
                  <td className="!px-2 text-right font-mono text-[9.5px] text-stone-600">{rowIndex + 1}</td>
                )}
                <td className="max-w-[240px] !px-2">
                  {href ? (
                    <Link
                      to={href}
                      title={`Open ${label}'s profile`}
                      className="block truncate text-[11.5px] text-stone-100 underline decoration-amber-400/20 underline-offset-[3px] hover:text-amber-200"
                    >
                      {label}
                    </Link>
                  ) : onSelectRow ? (
                    <button
                      type="button"
                      onClick={() => onSelectRow(row.key)}
                      title={`Narrow the cohort to ${label}`}
                      className={clsx(
                        'group inline-flex max-w-full items-center gap-1 truncate text-left text-[11.5px] hover:text-amber-200',
                        row.key == null ? 'italic text-stone-500' : 'text-stone-200',
                      )}
                    >
                      <span className="truncate">{label}</span>
                      <Plus size={9} className="flex-shrink-0 text-stone-700 group-hover:text-amber-300" aria-hidden="true" />
                    </button>
                  ) : (
                    <span className={clsx('truncate text-[11.5px]', row.key == null ? 'italic text-stone-500' : 'text-stone-200')}>
                      {label}
                    </span>
                  )}
                </td>
                {columns.map((column) => {
                  const ids = idsForMeasure(row, column.key, by, facts)
                  const figure = cellFigure(row.measures, column.key, ids, `${prefix}${label} — ${MEASURE_BY_KEY[column.key].label}`)
                  return (
                    <td key={column.key} className={clsx('!px-2 text-right font-mono text-[11px] tabular-nums', column.className)}>
                      <FigureValue figure={figure} align="right" />
                      {figure.basis && MEASURE_BY_KEY[column.key].coverage && figure.value != null && (
                        <div className="text-[8.5px] leading-none text-stone-600">{figure.basis}</div>
                      )}
                    </td>
                  )
                })}
                {extraColumns.map((column) => (
                  <td key={column.key} className={clsx('!px-2 text-right font-mono text-[10.5px] tabular-nums text-stone-400', column.className)}>
                    {column.render(row)}
                  </td>
                ))}
                {rowAction && <td className="!px-2 text-right">{rowAction(row)}</td>}
              </tr>
            )
          })}
        </tbody>
        {totals && (
          <tfoot>
            <tr className="border-t border-amber-400/15 bg-[#0b0b0b]">
              {showRank && <td />}
              <td className="!px-2 font-mono text-[9px] uppercase tracking-[0.16em] text-stone-400">Total</td>
              {columns.map((column) => {
                const ids = idsForMeasure({ measures: totals }, column.key, null, facts)
                const figure = cellFigure(totals, column.key, ids, `${prefix}All — ${MEASURE_BY_KEY[column.key].label}`)
                return (
                  <td key={column.key} className={clsx('!px-2 text-right font-mono text-[11px] font-medium tabular-nums text-stone-100', column.className)}>
                    <FigureValue figure={figure} align="right" />
                    {figure.basis && MEASURE_BY_KEY[column.key].coverage && figure.value != null && (
                      <div className="text-[8.5px] leading-none text-stone-600">{figure.basis}</div>
                    )}
                  </td>
                )
              })}
              {extraColumns.map((column) => (
                <td key={column.key} className={column.className} />
              ))}
              {rowAction && <td />}
            </tr>
          </tfoot>
        )}
      </table>
      {emptyColumns.length > 0 && !hideEmptyNote && (
        <p className="px-2 py-1 text-[9.5px] text-stone-600">
          No value on any row, so not shown: {emptyColumns.map((column) => MEASURE_BY_KEY[column.key].label).join(', ')}.
        </p>
      )}
    </div>
  )
}
